import React, { useEffect, useMemo, useRef, useState } from "react";
import QuickExpenseButton from "@/components/QuickExpenseButton";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import CardStatusChanger from "@/components/CardStatusChanger";
import { Search, Plus, FileText, Handshake, Building2, Calendar, ChevronRight, SlidersHorizontal, X, Pencil, Clock, Wallet, StickyNote, Shield, BadgeCheck, Check, CheckCircle2, Loader2, Lock } from "lucide-react";
import { api, BRL, formatDateBR, STATUS_LABELS } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import PendingPaperworkAlert from "@/components/PendingPaperworkAlert";
import SchoolCombobox from "@/components/SchoolCombobox";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScheduleChips } from "@/components/BudgetSchedule";

// Palette for school avatar circles (bg + icon color) — deterministic from school name
const SCHOOL_PALETTE = [
  { bg: "bg-violet-100", icon: "text-violet-600" },
  { bg: "bg-emerald-100", icon: "text-emerald-600" },
  { bg: "bg-amber-100", icon: "text-amber-600" },
  { bg: "bg-rose-100", icon: "text-rose-600" },
  { bg: "bg-sky-100", icon: "text-sky-600" },
  { bg: "bg-fuchsia-100", icon: "text-fuchsia-600" },
  { bg: "bg-indigo-100", icon: "text-indigo-600" },
  { bg: "bg-teal-100", icon: "text-teal-600" },
];

function schoolColor(name = "") {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return SCHOOL_PALETTE[h % SCHOOL_PALETTE.length];
}

// Statuses in which a FDP budget is no longer "active" (historical only).
// Mirrors FDP_HISTORICAL_STATUSES in backend/server.py.
const FDP_HISTORICAL = ["nota_fiscal_gerada", "nota_faturada", "reprovado", "deletado", "ex_sistema", "validade_expirada"];
const isActiveFdp = (b) => b.faz_depois_paga && !FDP_HISTORICAL.includes(b.status);

// Chip definitions — status chips EXCLUDE active FDP (fluxo distinto). FDP tem chip próprio.
// Após NFe o orçamento sai do FDP ativo e aparece só em "Nota gerada" / "Nota paga".
const CHIPS = [
  { key: "all", label: "Todos", match: () => true, active: "bg-[#7c3aed] text-white border-[#7c3aed]", count: "bg-white/20 text-white" },
  { key: "programados", label: "Programados", match: () => true, active: "bg-sky-50 text-sky-800 border-sky-200", count: "bg-sky-600 text-white" },
  { key: "aguardando", label: "Aguardando", match: (b) => b.status === "aguardando" && !isActiveFdp(b), active: "bg-amber-50 text-amber-700 border-amber-200", count: "bg-amber-500 text-white" },
  { key: "aprovado", label: "Aprovados", match: (b) => b.status === "aprovado" && !isActiveFdp(b), active: "bg-emerald-50 text-emerald-700 border-emerald-200", count: "bg-emerald-500 text-white" },
  { key: "iniciando", label: "Iniciado", match: (b) => b.status === "iniciando" && !isActiveFdp(b) && !b.nfa_open, active: "bg-blue-50 text-blue-700 border-blue-200", count: "bg-blue-500 text-white" },
  { key: "finalizado", label: "Finalizados", match: (b) => b.status === "finalizado" && !isActiveFdp(b), active: "bg-slate-100 text-slate-700 border-slate-300", count: "bg-slate-700 text-white" },
  { key: "fdp", label: "Faz depois paga", match: isActiveFdp, active: "bg-violet-50 text-violet-700 border-violet-200", count: "bg-violet-500 text-white" },
  { key: "reprovado", label: "Reprovados", match: (b) => b.status === "reprovado", active: "bg-rose-50 text-rose-700 border-rose-200", count: "bg-rose-500 text-white" },
  { key: "validade_expirada", label: "Validade Expirada", match: (b) => b.status === "validade_expirada", active: "bg-orange-50 text-orange-800 border-orange-200", count: "bg-orange-500 text-white" },
  { key: "nota_fiscal_gerada", label: "Nota Fiscal Gerada", match: (b) => b.status === "nota_fiscal_gerada" && !b.nfa_open, active: "bg-indigo-50 text-indigo-700 border-indigo-200", count: "bg-indigo-500 text-white" },
  { key: "nota_faturada", label: "Nota Fiscal Paga", match: (b) => b.status === "nota_faturada" && !b.nfa_open, active: "bg-violet-50 text-violet-700 border-violet-200", count: "bg-violet-500 text-white" },
  { key: "gerado_por_fdp", label: "Gerado por FDP", match: (b) => b.faz_depois_paga && (b.status === "nota_fiscal_gerada" || b.status === "nota_faturada"), active: "bg-slate-100 text-slate-700 border-slate-300", count: "bg-slate-600 text-white" },
  { key: "deletado", label: "Deletado", match: (b) => b.status === "deletado", active: "bg-slate-100 text-slate-600 border-slate-300", count: "bg-slate-500 text-white" },
  { key: "nf_antecipada", label: "Nota gerada — em andamento", match: (b) => !!b.nfa_open, active: "bg-indigo-50 text-indigo-700 border-indigo-200", count: "bg-indigo-600 text-white" },
];

const STATUS_OPTIONS = [
  { v: "all", l: "Todos os status" },
  { v: "aguardando", l: "Aguardando" },
  { v: "aprovado", l: "Aprovado" },
  { v: "reprovado", l: "Reprovado" },
  { v: "iniciando", l: "Iniciado" },
  { v: "finalizado", l: "Finalizado" },
  { v: "nota_fiscal_gerada", l: "Nota Fiscal Gerada" },
  { v: "nota_faturada", l: "Nota Fiscal Paga" },
  { v: "deletado", l: "Deletado" },
  { v: "validade_expirada", l: "Validade Expirada" },
];

// Light tint per status — applied on card background when "Todos" chip is active, so
// users can scan multiple statuses visually without having to read each badge.
const STATUS_CARD_TINT = {
  aguardando: "bg-amber-50/60 border-amber-100",
  aprovado: "bg-emerald-50/60 border-emerald-100",
  reprovado: "bg-rose-50/60 border-rose-100",
  iniciando: "bg-blue-50/60 border-blue-100",
  finalizado: "bg-slate-50 border-slate-200",
  nota_fiscal_gerada: "bg-indigo-50/60 border-indigo-100",
  nota_faturada: "bg-violet-50/60 border-violet-100",
  ex_sistema: "bg-amber-100/40 border-amber-100",
  deletado: "bg-slate-100 border-slate-200",
  validade_expirada: "bg-orange-50 border-orange-200",
};

function budgetDescription(b) {
  const ref = (b.reference || "").trim();
  if (ref) return ref;
  return "Aqui será a referência do orçamento";
}

function BudgetCard({ b, expandedId, onToggle, tinted = false, onMarkPaid, onItemCompleted, onStatusChanged }) {
  const nav = useNavigate();
  const color = schoolColor(b.school_name);
  const open = expandedId === b.id;
  const tint = tinted ? (STATUS_CARD_TINT[b.status] || "bg-white border-slate-100") : "bg-white border-slate-100";
  const canMarkPaid = b.status === "nota_fiscal_gerada";

  // Items are loaded on-demand when the card is expanded (list endpoint doesn't ship items).
  const [items, setItems] = useState(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [completingId, setCompletingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    if (items !== null) return;
    let cancelled = false;
    (async () => {
      setLoadingItems(true);
      try {
        const r = await api.get(`/budgets/${b.id}`);
        if (!cancelled) setItems(r.data?.items || []);
      } catch (e) {
        if (!cancelled) toast.error("Erro ao carregar itens");
      } finally {
        if (!cancelled) setLoadingItems(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, b.id, items]);

  const playBeep = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = 880; o.type = "sine";
      g.gain.setValueAtTime(0.18, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
      o.start(); o.stop(ctx.currentTime + 0.36);
    } catch (_) {}
  };

  const completeItem = async (it) => {
    if (b.status !== "iniciando") {
      toast.error("O orçamento precisa estar 'Em andamento' para concluir itens.");
      return;
    }
    setCompletingId(it.id);
    try {
      const r = await api.post(`/budgets/${b.id}/items/${it.id}/complete-toggle`, {});
      playBeep();
      toast.success("Item concluído — escola notificada");
      setItems(r.data?.items || []);
      onItemCompleted?.(b.id, r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao concluir item");
    } finally {
      setCompletingId(null);
    }
  };

  const completedCount = b.items_completed ?? 0;
  const totalItems = b.items_count ?? 0;
  const pendingCount = Math.max(totalItems - completedCount, 0);
  const completedTotal = b.items_completed_total ?? 0;
  const pendingTotal = b.items_pending_total ?? 0;

  const openFull = (e) => {
    if (!e.currentTarget.contains(e.target)) return;
    if (e.target.closest("button, a, input, label, [data-no-nav]")) return;
    nav(`/orcamentos/${b.id}`);
  };

  return (
    <div
      data-testid={`card-budget-${b.number}`}
      onClick={openFull}
      className={`rounded-2xl border card-shadow overflow-hidden cursor-pointer ${tint}`}
    >
      <div className="p-3.5">
        {/* School name — full width, wraps */}
        <p className="font-heading text-sm font-black text-[#1e3a8a] uppercase tracking-tight leading-tight break-words">
          {b.school_name}
        </p>

        <div className="flex items-start gap-3 mt-2">
          <div className={`w-12 h-12 rounded-full ${color.bg} flex items-center justify-center shrink-0`}>
            <Building2 className={`w-6 h-6 ${color.icon}`} strokeWidth={2.2} />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[11px] text-slate-500 leading-tight">
                  N° {b.number} <span className="mx-1 text-slate-300">•</span> {formatDateBR(b.budget_date)}
                </p>
                <p className="text-[12px] text-slate-700 mt-1 line-clamp-1">{budgetDescription(b)}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold leading-none">Valor total</p>
                <p className="font-heading text-sm font-black text-slate-900 mt-1 whitespace-nowrap">
                  {BRL(b.total)}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 flex-wrap mt-2">
              <CardStatusChanger b={b} onChanged={onStatusChanged} />
              <ScheduleChips b={b} />
              {b.nfa_open && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-indigo-100 text-indigo-700 border border-indigo-200 px-2 py-0.5 rounded-full" data-testid={`badge-nfa-${b.id}`}>
                  <Lock className="w-3 h-3" /> Nota gerada · {b.items_completed ?? 0}/{b.items_count ?? 0} itens
                </span>
              )}
              {b.faz_depois_paga && isActiveFdp(b) && (
                <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full">
                  <Handshake className="w-3 h-3" /> Sistema faz depois paga
                </span>
              )}
              {b.faz_depois_paga && !isActiveFdp(b) && (
                <span
                  title="Este orçamento foi emitido pelo sistema Faz Depois Paga"
                  className="inline-flex items-center gap-1 text-[10px] font-semibold bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-full"
                >
                  <Handshake className="w-3 h-3" /> Emitido via FDP
                </span>
              )}
            </div>

            <div className="flex items-center justify-between mt-2.5 gap-2">
              <div className="min-w-0 flex-1 space-y-0.5 text-[11px] text-slate-500">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1">
                    <FileText className="w-3.5 h-3.5" /> {totalItems} itens
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" /> Validade: {formatDateBR(b.validity_date)}
                  </span>
                </div>
                {totalItems > 0 && (
                  <div className="flex items-center gap-x-2 gap-y-0.5 flex-wrap text-[10.5px]">
                    {completedCount > 0 && (
                      <span className="inline-flex items-center gap-1 text-emerald-700">
                        <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
                        <strong>{completedCount}</strong> concluído{completedCount > 1 ? "s" : ""} {BRL(completedTotal)}
                      </span>
                    )}
                    {pendingCount > 0 && (
                      <span className="inline-flex items-center gap-1 text-orange-700">
                        <Clock className="w-3 h-3" strokeWidth={2.5} />
                        falta <strong>{pendingCount}</strong> {BRL(pendingTotal)}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => onToggle(b.id)}
                data-testid={`btn-ver-orcamento-${b.number}`}
                className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-3 py-1 border transition-colors ${open ? "bg-[#7c3aed] text-white border-[#7c3aed]" : "bg-violet-50 text-[#7c3aed] border-violet-100 hover:bg-violet-100"}`}
              >
                {open ? "Ocultar" : "Ver detalhes"}
                <ChevronRight className={`w-3 h-3 transition-transform ${open ? "rotate-90" : ""}`} />
              </button>
            </div>
            {canMarkPaid && (
              <div className="mt-2.5 flex">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onMarkPaid?.(b); }}
                  data-testid={`btn-mark-paid-${b.number}`}
                  className="inline-flex items-center gap-1.5 text-[11px] font-bold rounded-lg px-3 py-1.5 bg-violet-600 text-white hover:bg-violet-700 border border-violet-700 transition-colors"
                >
                  <BadgeCheck className="w-3.5 h-3.5" />
                  Marcar como pago
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {open && (
        <div data-no-nav className="border-t border-slate-100 bg-slate-50/60 px-4 py-3 space-y-3 cursor-default" data-testid={`budget-details-${b.number}`}>
          {/* Summary rows */}
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white rounded-xl border border-slate-100 p-2.5">
              <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1">
                <Clock className="w-3 h-3" /> Entrega
              </p>
              <p className="text-[12px] font-semibold text-slate-800 mt-0.5">{b.delivery_days || 0} dias</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 p-2.5">
              <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1">
                <Shield className="w-3 h-3" /> Garantia
              </p>
              <p className="text-[12px] font-semibold text-slate-800 mt-0.5 truncate">{b.warranty || "—"}</p>
            </div>
            {b.payment_option && (
              <div className="bg-white rounded-xl border border-slate-100 p-2.5 col-span-2">
                <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1">
                  <Wallet className="w-3 h-3" /> Pagamento
                </p>
                <p className="text-[12px] font-semibold text-slate-800 mt-0.5">
                  {b.payment_option}
                  {b.payment_other ? ` — ${b.payment_other}` : ""}
                </p>
              </div>
            )}
          </div>

          {/* Items — fetched on expand, with inline Concluir button */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-1">
              Itens do orçamento {items ? `(${items.length})` : ""}
            </p>
            {loadingItems ? (
              <div className="bg-white rounded-xl border border-slate-100 p-4 flex items-center justify-center text-xs text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Carregando itens…
              </div>
            ) : items && items.length > 0 ? (
              <ul className="bg-white rounded-xl border border-slate-100 divide-y divide-slate-100">
                {items.map((it) => {
                  const done = !!it.completed;
                  const canToggle = b.status === "iniciando";
                  return (
                    <li key={it.id} className={`p-2.5 ${done ? "bg-emerald-50/40" : ""}`}>
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <p className={`text-[12px] ${done ? "text-slate-500 line-through" : "text-slate-800"} line-clamp-3`}>
                            {it.description}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-0.5">
                            Qtde {it.quantity} × {BRL(it.unit_value || 0)}
                            {done && it.completed_at ? ` · concluído em ${formatDateBR(it.completed_at)}` : ""}
                          </p>
                        </div>
                        <p className="text-[12px] font-semibold text-slate-900 whitespace-nowrap shrink-0">
                          {BRL((it.quantity || 0) * (it.unit_value || 0))}
                        </p>
                      </div>
                      <div className="mt-2 flex justify-end">
                        {done ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 rounded-full px-2.5 py-1">
                            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.5} /> Concluído
                          </span>
                        ) : canToggle ? (
                          <button
                            type="button"
                            onClick={() => completeItem(it)}
                            disabled={completingId === it.id}
                            data-testid={`btn-complete-item-${b.number}-${it.id}`}
                            className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-3 py-1 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 transition-colors"
                          >
                            {completingId === it.id ? (
                              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Concluindo…</>
                            ) : (
                              <><Check className="w-3.5 h-3.5" strokeWidth={3} /> Concluir</>
                            )}
                          </button>
                        ) : (
                          <span className="text-[10px] text-slate-400 italic">
                            Só pode concluir quando "Em andamento"
                          </span>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="bg-white rounded-xl border border-slate-100 p-3 text-center text-[11px] text-slate-500">
                Nenhum item cadastrado neste orçamento.
              </div>
            )}
          </div>

          {b.observation && (
            <div className="bg-white rounded-xl border border-slate-100 p-2.5">
              <p className="text-[9px] uppercase tracking-wider text-slate-400 font-semibold flex items-center gap-1">
                <StickyNote className="w-3 h-3" /> Observação
              </p>
              <p className="text-[12px] text-slate-700 mt-0.5 whitespace-pre-wrap break-words">{b.observation}</p>
            </div>
          )}

          {!["nota_fiscal_gerada", "nota_faturada"].includes(b.status) && <QuickExpenseButton budget={b} />}

          <Link to={`/orcamentos/${b.id}`}>
            <Button className="w-full bg-[#7c3aed] hover:bg-[#6d28d9] text-white rounded-xl h-10" data-testid={`btn-abrir-orcamento-${b.number}`}>
              <Pencil className="w-4 h-4 mr-2" />
              Abrir orçamento completo
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

export default function BudgetsList({ fdpOnly = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const chipsRef = useRef(null);
  const PAGE_SIZE = 50;
  const initialChip = (() => {
    if (fdpOnly) return "fdp";
    const s = searchParams.get("status");
    if (s && CHIPS.some((c) => c.key === s)) return s;
    return "all";
  })();
  const [budgets, setBudgets] = useState([]);
  const [schools, setSchools] = useState([]);
  const [q, setQ] = useState("");
  const [chip, setChip] = useState(initialChip);
  useEffect(() => {
    const box = chipsRef.current;
    const el = box?.querySelector('[data-active="1"]');
    if (box && el) {
      const left = el.getBoundingClientRect().left - box.getBoundingClientRect().left + box.scrollLeft;
      box.scrollTo({ left: left - (box.clientWidth - el.offsetWidth) / 2, behavior: "smooth" });
    }
  }, [chip]);
  const [advStatus, setAdvStatus] = useState("all"); // advanced filter overrides chip
  const [advFdp, setAdvFdp] = useState("any");
  const [advSchool, setAdvSchool] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [offset, setOffset] = useState(0);
  // Latest-wins guard: discard stale responses when the user flips chips/filters fast.
  const loadSeqRef = React.useRef(0);

  // Map a UI chip to the server-side filter params it represents.
  const chipToParams = (key) => {
    if (key === "all") return {};
    if (key === "programados") return { programados: "true" };
    return { ...chipToParamsBase(key), exclude_programados: "true" };
  };
  const chipToParamsBase = (key) => {
    if (key === "fdp") return { faz_depois_paga: "true" };
    if (key === "gerado_por_fdp") return { gerado_por_fdp: "true" };
    if (key === "nf_antecipada") return { nf_antecipada: "true" };
    if (key === "nota_fiscal_gerada" || key === "nota_faturada") return { status: key, exclude_nfa: "true" };
    if (["aguardando", "aprovado", "iniciando", "finalizado"].includes(key)) {
      // "live" statuses exclude FDP ativo (chip definitions mirror this)
      return { status: key, faz_depois_paga: "false" };
    }
    return { status: key };
  };

  const buildParams = (nextOffset = 0) => {
    const params = { format: "paged", limit: PAGE_SIZE, offset: nextOffset };
    if (q) params.q = q;
    if (showFilters) {
      if (advStatus !== "all") params.status = advStatus;
      if (advFdp === "true") params.faz_depois_paga = "true";
      else if (advFdp === "false") params.faz_depois_paga = "false";
      if (advSchool) params.school_id = advSchool;
    } else if (fdpOnly) {
      params.faz_depois_paga = "true";
    } else {
      Object.assign(params, chipToParams(chip));
    }
    return params;
  };

  const load = async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const r = await api.get("/budgets", { params: buildParams(0) });
      if (seq !== loadSeqRef.current) return;
      const data = r.data || {};
      setBudgets(data.items || []);
      setTotal(data.total || 0);
      setCounts(data.counts || {});
      setOffset(0);
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  };

  const loadMore = async () => {
    const nextOffset = budgets.length;
    const seq = loadSeqRef.current;
    setLoadingMore(true);
    try {
      const r = await api.get("/budgets", { params: buildParams(nextOffset) });
      if (seq !== loadSeqRef.current) return;
      const data = r.data || {};
      setBudgets((prev) => [...prev, ...(data.items || [])]);
      setOffset(nextOffset);
    } finally {
      setLoadingMore(false);
    }
  };

  // Load schools list once (used by the advanced school filter combobox)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/schools");
        setSchools(r.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [chip, advStatus, advFdp, advSchool, showFilters]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q]);

  // Sync chip with ?status= URL param (so Dashboard links pre-select the right chip)
  useEffect(() => {
    if (fdpOnly) return;
    const s = searchParams.get("status");
    if (s && CHIPS.some((c) => c.key === s)) {
      setChip(s);
      setShowFilters(false);
      setAdvStatus("all");
      setAdvFdp("any");
    }
  }, [searchParams, fdpOnly]);

  const markAsPaid = async (b) => {
    if (!window.confirm(`Dar baixa no orçamento Nº ${b.number} como PAGO?`)) return;
    try {
      await api.put(`/budgets/${b.id}`, { status: "nota_faturada" });
      toast.success(`Orçamento Nº ${b.number} marcado como pago`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao marcar como pago");
    }
  };

  // Sort: active statuses first, then ex_sistema/deletado at the end
  // Within each group, newest first (by budget_date desc)
  const filtered = useMemo(() => {
    const bucket = (b) => {
      if (b.status === "ex_sistema") return 2;
      if (b.status === "deletado") return 3;
      return 1;
    };
    return [...budgets].sort((a, b) => {
      const ba = bucket(a), bb = bucket(b);
      if (ba !== bb) return ba - bb;
      return String(b.budget_date || "").localeCompare(String(a.budget_date || ""));
    });
  }, [budgets]);

  return (
    <div className="space-y-4" data-testid="budgets-page">
      {/* Header */}
      <div className="bg-white rounded-2xl border border-slate-100 card-shadow p-4 flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-violet-100 flex items-center justify-center shrink-0">
          <FileText className="w-5 h-5 text-[#7c3aed]" strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-xl md:text-2xl font-black text-[#1e3a8a] leading-tight">
            {fdpOnly ? "Faz Depois Paga" : "Orçamentos"}
          </h1>
          <p className="text-[12px] md:text-sm text-slate-500 leading-tight mt-0.5">
            {fdpOnly ? "Orçamentos no sistema Faz Depois Paga" : "Visualize, filtre e gerencie todos os orçamentos."}
          </p>
        </div>
        <Link to="/orcamentos/novo" className="shrink-0">
          <Button className="bg-[#7c3aed] hover:bg-[#6d28d9] text-white rounded-xl h-10 px-3 md:px-4" data-testid="btn-novo-orcamento">
            <Plus className="w-4 h-4 md:mr-2" />
            <span className="hidden md:inline">Novo orçamento</span>
          </Button>
        </Link>
      </div>

      {/* Pending-paperwork red alert */}
      {!fdpOnly && <PendingPaperworkAlert onChanged={load} />}

      {/* Status chips (horizontal scroll on mobile) */}
      {!fdpOnly && (
        <div ref={chipsRef} className="-mx-1 overflow-x-auto no-scrollbar">
          <div className="flex gap-2 px-1 pb-1 min-w-max">
            {CHIPS.map((c) => {
              const isActive = chip === c.key && !showFilters;
              const inactive =
                "bg-white text-slate-700 border-slate-200 hover:border-slate-300";
              return (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => {
                    setChip(c.key);
                    setShowFilters(false);
                    setAdvStatus("all");
                    setAdvFdp("any");
                    if (c.key === "all") setSearchParams({}, { replace: true });
                    else setSearchParams({ status: c.key }, { replace: true });
                  }}
                  data-testid={`chip-${c.key}`}
                  data-active={isActive ? "1" : "0"}
                  className={`inline-flex items-center gap-2 border rounded-xl px-3 py-2 text-[12px] font-semibold transition-colors ${isActive ? c.active : inactive}`}
                >
                  <span>{c.label}</span>
                  <span className={`min-w-[22px] h-5 inline-flex items-center justify-center rounded-full text-[11px] font-bold px-1.5 ${isActive ? c.count : "bg-slate-100 text-slate-600"}`}>
                    {counts[c.key] ?? 0}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Search + filtros */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por escola, número ou descrição..."
            className="pl-9 h-11 rounded-xl bg-white"
            data-testid="input-search-orcamentos"
          />
        </div>
        {!fdpOnly && (
          <button
            type="button"
            onClick={() => setShowFilters((s) => !s)}
            data-testid="btn-toggle-filters"
            className={`shrink-0 inline-flex items-center gap-2 h-11 px-3 rounded-xl border text-[12px] font-semibold transition-colors ${showFilters ? "bg-[#7c3aed] text-white border-[#7c3aed]" : "bg-white text-[#7c3aed] border-violet-200 hover:bg-violet-50"}`}
          >
            {showFilters ? <X className="w-4 h-4" /> : <SlidersHorizontal className="w-4 h-4" />}
            Filtros
          </button>
        )}
      </div>

      {/* Advanced filters */}
      {showFilters && !fdpOnly && (
        <div className="bg-white rounded-xl border border-slate-100 card-shadow p-3 flex flex-col md:flex-row gap-2">
          <div className="w-full md:w-80">
            <SchoolCombobox
              schools={[{ id: "", name: "Todas as escolas", nickname: "" }, ...schools]}
              value={advSchool}
              onChange={(id) => setAdvSchool(id || "")}
              placeholder="Filtrar por escola..."
            />
          </div>
          <Select value={advStatus} onValueChange={setAdvStatus}>
            <SelectTrigger className="w-full md:w-56" data-testid="select-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={advFdp} onValueChange={setAdvFdp}>
            <SelectTrigger className="w-full md:w-56" data-testid="select-fdp-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Comuns + Faz Depois Paga</SelectItem>
              <SelectItem value="false">Somente comuns</SelectItem>
              <SelectItem value="true">Somente Faz Depois Paga</SelectItem>
            </SelectContent>
          </Select>
          {advSchool && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setAdvSchool("")}
              className="shrink-0 h-9"
              data-testid="btn-clear-school-filter"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Escola
            </Button>
          )}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="text-center py-10 text-slate-500">Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 card-shadow p-12 text-center">
          <FileText className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-600 font-medium">Nenhum orçamento encontrado</p>
          <p className="text-sm text-slate-500 mt-1">Ajuste os filtros ou crie um novo orçamento.</p>
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((b) => (
            <BudgetCard
              key={b.id}
              b={b}
              expandedId={expandedId}
              onToggle={(id) => setExpandedId((cur) => (cur === id ? null : id))}
              tinted={!fdpOnly && !showFilters && chip === "all"}
              onMarkPaid={markAsPaid}
              onItemCompleted={() => load()}
              onStatusChanged={() => load()}
            />
          ))}
          {/* Pagination footer */}
          <div className="flex flex-col items-center gap-2 pt-3">
            <p className="text-[11px] text-slate-500">
              Mostrando <strong>{budgets.length}</strong> de <strong>{total}</strong>
            </p>
            {budgets.length < total && (
              <Button
                type="button"
                variant="outline"
                onClick={loadMore}
                disabled={loadingMore}
                className="h-10 px-6 rounded-xl border-slate-200 text-slate-700 font-semibold"
                data-testid="btn-load-more-budgets"
              >
                {loadingMore ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Carregando…</>
                ) : (
                  <>Carregar mais orçamentos</>
                )}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
